import { useState } from 'react';
import { Alert, Empty, Flex, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Icon from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import EditIcon from '@/assets/icons/pencil-solid-full.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-01.svg?react';
import { Button } from '@/components/button/button';
import Callout from '@/components/callout/callout';
import IconButton from '@/components/icon-button/icon-button';
import StatusBadge from '@/components/status-badge/status-badge';
import Toggle from '@/components/toggle/toggle';
import VsTable from '@/components/vs-table/vs-table';
import {
  useDeleteSalesAgent,
  useSalespeople,
  useUpdateSalesAgent,
} from '@/hooks/use-salespeople/use-salespeople';
import { SalesAgent } from '@/services/outbound-calls-service/salespeople.service.types';
import { openNotification } from '@/utils/notification/notification';
import { useOutboundContext } from '../outbound-calls.context';
import { outboundErrorMessage } from '../outbound-calls.messages';
import { SALES_AGENT_STATUS_BADGE, formatDateTime, workHoursLabel } from '../outbound-calls.utils';
import { SalespersonFormModal } from './salesperson-form-modal';
import styles from '../outbound-calls.module.scss';

type FormState = { open: false } | { open: true; salesAgent?: SalesAgent };

/** A busy salesperson stays busy; the switch decides where they go once the call ends. */
const isSwitchedOn = (salesAgent: SalesAgent) =>
  salesAgent.status === 'available' ||
  (salesAgent.status === 'busy' && salesAgent.available_until !== null);

/** Who can take a transferred call. A campaign dials only while one of them is available. */
export function Salespeople() {
  const { t } = useTranslation(['outbound', 'common']);
  const { company } = useOutboundContext();
  const { salespeople, isLoading, error } = useSalespeople(company.id);
  const { updateSalesAgent, pendingId } = useUpdateSalesAgent();
  const { deleteSalesAgent, isPending: isDeleting } = useDeleteSalesAgent();
  const [form, setForm] = useState<FormState>({ open: false });
  const [toDelete, setToDelete] = useState<SalesAgent | undefined>();

  const failed = (requestError: unknown) =>
    openNotification({
      type: 'error',
      message: outboundErrorMessage(requestError, t('outbound:actionFailed')),
    });

  const toggleAvailability = (salesAgent: SalesAgent, available: boolean) =>
    updateSalesAgent({
      id: salesAgent.id,
      payload: { status: available ? 'available' : 'offline' },
    }).catch(failed);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteSalesAgent(toDelete.id);
      setToDelete(undefined);
    } catch (deleteError) {
      failed(deleteError);
    }
  };

  const columns: ColumnsType<SalesAgent> = [
    {
      title: t('outbound:salespeople.columns.name'),
      key: 'name',
      render: (_, salesAgent) => (
        <span className={styles.stack}>
          <b>{salesAgent.name}</b>
          <span className={styles.secondary}>
            {salesAgent.user_email ?? t('outbound:salespeople.noAccount')}
          </span>
        </span>
      ),
    },
    { title: t('outbound:salespeople.columns.phone'), dataIndex: 'phone', key: 'phone' },
    {
      title: t('outbound:salespeople.columns.status'),
      key: 'status',
      render: (_, salesAgent) => (
        <span className={styles.stack}>
          <StatusBadge
            variant={SALES_AGENT_STATUS_BADGE[salesAgent.status]}
            label={t(`outbound:salesAgentStatus.${salesAgent.status}`)}
          />
          {salesAgent.status === 'available' && !salesAgent.available_now && (
            <span className={styles.secondary}>{t('outbound:salespeople.outsideHours')}</span>
          )}
          {salesAgent.available_until && salesAgent.available_now && (
            <span className={styles.secondary}>
              {t('outbound:salespeople.availableUntil', {
                at: formatDateTime(salesAgent.available_until),
              })}
            </span>
          )}
        </span>
      ),
    },
    {
      title: t('outbound:salespeople.columns.available'),
      key: 'available',
      render: (_, salesAgent) => (
        <Toggle
          checked={isSwitchedOn(salesAgent)}
          disabled={pendingId === salesAgent.id}
          label={t('outbound:salespeople.toggle', { name: salesAgent.name })}
          onChange={(checked) => toggleAvailability(salesAgent, checked)}
        />
      ),
    },
    {
      title: t('outbound:salespeople.columns.hours'),
      key: 'hours',
      render: (_, salesAgent) => workHoursLabel(salesAgent),
    },
    {
      title: t('outbound:salespeople.columns.changedAt'),
      key: 'status_changed_at',
      render: (_, salesAgent) => formatDateTime(salesAgent.status_changed_at),
    },
    {
      key: 'actions',
      align: 'right',
      render: (_, salesAgent) => (
        <Flex gap={8} justify="flex-end">
          <IconButton
            label={t('outbound:salespeople.edit', { name: salesAgent.name })}
            onClick={() => setForm({ open: true, salesAgent })}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            label={t('outbound:salespeople.delete', { name: salesAgent.name })}
            loading={isDeleting && toDelete?.id === salesAgent.id}
            onClick={() => setToDelete(salesAgent)}
          >
            <TrashIcon />
          </IconButton>
        </Flex>
      ),
    },
  ];

  return (
    <Flex vertical gap={14}>
      <div className={styles.toolbar}>
        <Callout className={styles.grow}>{t('outbound:salespeople.hint')}</Callout>
        <Button buttonVariant="primary" onClick={() => setForm({ open: true })}>
          <Icon component={() => <PlusIcon className={styles.buttonIcon} />} />
          {t('outbound:salespeople.add')}
        </Button>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={outboundErrorMessage(error, t('outbound:errors.load'))}
        />
      ) : (
        <VsTable<SalesAgent>
          rowKey="id"
          loading={isLoading}
          dataSource={salespeople}
          columns={columns}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('outbound:salespeople.empty')}
              />
            ),
          }}
        />
      )}

      <SalespersonFormModal
        open={form.open}
        companyId={company.id}
        salesAgent={form.open ? form.salesAgent : undefined}
        onClose={() => setForm({ open: false })}
      />

      <Modal
        open={!!toDelete}
        title={t('outbound:salespeople.deleteTitle')}
        okText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
        okButtonProps={{ danger: true, loading: isDeleting }}
        onOk={confirmDelete}
        onCancel={() => setToDelete(undefined)}
      >
        <p>{t('outbound:salespeople.deleteContent', { name: toDelete?.name ?? '' })}</p>
      </Modal>
    </Flex>
  );
}
